import React, { useState, useEffect } from "react";
import Sidebar from "../Components/Sidebar";
import Constant from "../Components/Utils/Constant";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiSearch, FiUser, FiCreditCard } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

export default function PayUser() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [allPlans, setAllPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [loading, setLoading] = useState(false);

  // Fetch all users and plans on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        console.log("Fetching users and plans. Token present:", !!token);

        // 1. Fetch Users
        const userRes = await axios.get(`${Constant.BASE_URL}/user/get`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const usersList = userRes.data?.admins || [];
        console.log("Fetched users count:", usersList.length);
        setUsers(usersList);

        // 2. Fetch Subscription Plans
        const planRes = await axios.get(`${Constant.BASE_URL}/subscription/get`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const plansList = planRes.data?.subscription || [];
        console.log("Fetched plans count:", plansList.length);
        setAllPlans(plansList);

      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load users or subscription plans.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter users based on search query
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredUsers([]);
    } else {
      const lower = searchTerm.toLowerCase();
      const results = users.filter(
        (u) =>
          (u.name || "").toLowerCase().includes(lower) ||
          (u.email || "").toLowerCase().includes(lower) ||
          (u.phone || "").toLowerCase().includes(lower)
      );
      setFilteredUsers(results.slice(0, 10)); // Limit to top 10 results for dropdown
    }
  }, [searchTerm, users]);

  // Handle user selection & filter subscription plans
  const handleSelectUser = (user) => {
    console.log("Selected user:", user);
    setSelectedUser(user);
    setSearchTerm(user.name);
    setIsDropdownOpen(false);
    setSelectedPlan(null); // Reset plan on user change

    // Normalize user's role to match visibility
    const userRole = (user.role || "User").toLowerCase();
    console.log("Normalizing role for filtering plans:", userRole);
    console.log("Available plans visibility lists:", allPlans.map(p => ({name: p.planname, visibility: p.planevisibility})));
    
    const matchedPlans = allPlans.filter((plan) => {
      const visibilities = plan.planevisibility || [];
      return visibilities.some((vis) => {
        const normalizedVis = vis.toLowerCase().replace(/\s+/g, "");
        const normalizedRole = userRole.replace(/\s+/g, "");
        
        // Handle "modelmanager" / "model manager" mapping
        if (normalizedRole === "modelmanager" && normalizedVis === "modelmanager") return true;
        return normalizedVis === normalizedRole;
      });
    });

    console.log("Matched plans for user role:", matchedPlans);
    setFilteredPlans(matchedPlans);
  };

  const handlePay = async () => {
    if (!selectedUser) {
      toast.warning("Please select a user first.");
      return;
    }
    if (!selectedPlan) {
      toast.warning("Please select a subscription plan.");
      return;
    }

    // If price is 0, activate the subscription directly
    if (Number(selectedPlan.price || 0) === 0) {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${Constant.BASE_URL}/subscription/activate-free/${selectedUser._id}`,
          { planId: selectedPlan._id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          toast.success("Free subscription activated successfully!");
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
        } else {
          toast.error("Failed to activate free subscription.");
        }
      } catch (error) {
        console.error("Free activation error:", error);
        toast.error("An error occurred during activation.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Open the payment selector page for paid plans
    const paymentUrl = `${Constant.BASE_URL}/payment-selector?subscriptionId=${selectedPlan._id}&userId=${selectedUser._id}`;
    window.open(paymentUrl, "_blank");
  };

  return (
    <Sidebar>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="p-3 overflow-auto bg-grey-300 lg:p-3 xl:p-3 2xl:p-6">
        <div
          className="bg-white h-[78.55vh] p-6 lg:p-10 overflow-y-auto"
          style={{ borderRadius: "0px 0px 11px 11px" }}
        >
          <div className="w-full max-w-3xl mx-auto bg-white border border-gray-200 rounded-xl shadow-lg p-6 lg:p-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 border-b border-gray-100 pb-4">
              Pay User
            </h2>

            {/* Search User Input */}
            <div className="relative mb-6">
              <label className="block text-gray-700 text-sm font-semibold mb-2">
                Search User (Name, Email or Phone)
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                  placeholder="Type to search user..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  onFocus={() => setIsDropdownOpen(true)}
                />
                <FiSearch className="absolute left-3 top-3 text-gray-400 text-lg" />
              </div>

              {/* Search Suggestions Dropdown */}
              {isDropdownOpen && filteredUsers.length > 0 && (
                <ul className="absolute z-20 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-lg max-h-60 overflow-y-auto divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <li
                      key={user._id}
                      className="px-4 py-3 hover:bg-orange-50 cursor-pointer flex flex-col transition"
                      onClick={() => handleSelectUser(user)}
                    >
                      <span className="font-semibold text-gray-800">{user.name}</span>
                      <span className="text-xs text-gray-500">{user.email} | {user.phone}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* User Info Details Panel */}
            {selectedUser && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-6">
                <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FiUser className="text-[#FF9900]" /> User Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 block">Name</span>
                    <span className="font-semibold text-gray-800">{selectedUser.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Phone</span>
                    <span className="font-semibold text-gray-800">{selectedUser.phone}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Email</span>
                    <span className="font-semibold text-gray-800">{selectedUser.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">User Type / Role</span>
                    <span className="inline-block bg-orange-100 text-[#FF9900] px-2.5 py-0.5 rounded-full font-semibold text-xs mt-1">
                      {selectedUser.role || "User"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Subscription Status</span>
                    <span className={`font-semibold ${selectedUser.subscriptionexpiry ? "text-red-500" : "text-green-600"}`}>
                      {selectedUser.subscriptionexpiry ? "Expired / Inactive" : "Active"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Selection Dropdown */}
            {selectedUser && (
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-semibold mb-2">
                  Select Subscription Plan
                </label>
                {filteredPlans.length > 0 ? (
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                    value={selectedPlan ? selectedPlan._id : ""}
                    onChange={(e) => {
                      const plan = filteredPlans.find((p) => p._id === e.target.value);
                      setSelectedPlan(plan || null);
                    }}
                  >
                    <option value="" disabled>-- Select a Plan --</option>
                    {filteredPlans.map((plan) => (
                      <option key={plan._id} value={plan._id}>
                        {plan.planname} (₹{plan.price})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-red-500 italic">No plans available for user role "{selectedUser.role || "User"}"</p>
                )}
              </div>
            )}

            {/* Selected Subscription Details */}
            {selectedPlan && (
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-5 mb-6 flex justify-between items-center">
                <div>
                  <h4 className="text-sm text-gray-500 font-medium">Selected Plan</h4>
                  <span className="text-lg font-bold text-gray-800">{selectedPlan.planname}</span>
                  <div className="text-xs text-gray-500 mt-1">
                    Validity: {selectedPlan.planevalidity} {selectedPlan.validityUnit || 'days'}
                  </div>
                </div>
                <div className="text-right">
                  <h4 className="text-sm text-gray-500 font-medium">Cost</h4>
                  <span className="text-2xl font-black text-[#FF9900]">₹{selectedPlan.price}</span>
                </div>
              </div>
            )}

            {/* Action Pay Button */}
            <button
              onClick={handlePay}
              disabled={!selectedUser || !selectedPlan || loading}
              className={`w-full py-3 rounded-lg font-bold text-white transition-all shadow-md flex items-center justify-center gap-2 ${
                selectedUser && selectedPlan && !loading
                  ? "bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 active:scale-[0.98]"
                  : "bg-gray-300 cursor-not-allowed shadow-none"
              }`}
            >
              <FiCreditCard className="text-lg" />
              {loading ? "Activating..." : (selectedPlan && Number(selectedPlan.price || 0) === 0 ? "Activate Plan" : "Pay Now")}
            </button>
          </div>
        </div>
      </div>
    </Sidebar>
  );
}
